import * as React from "react";
import { Box, Drawer, Grid, IconButton, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import ToolBarNavigation from "./ToolBarNavigation";
import Breadcrumbs from "./Breadcrumbs";
import { useToolBar } from "../contexts/ToolBarProvider";
import { ThemeModeProvider } from "../contexts/ThemeModeProvider";
import { useTokens } from "../theme";
import SessionExpiryWarning from "./SessionExpiryWarning";
import KeyboardShortcuts from "./KeyboardShortcuts";
import { useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import useNotifications from "../hooks/useNotifications";

const PAGE_TITLES = {
  clientDashboard: "Clients",
  adminTable: "Management",
  groupDashboard: "Groups",
  tutorials: "Tutorials",
  settings: "Settings",
};

// Scopes light/dark mode to the authenticated app shell only (nav bar +
// everything right of it). Pre-auth pages (Login, CreateNewAccount, etc.)
// never mount this provider, so they always render with the base light theme.
export default function DashboardLayout({ children }) {
  return (
    <ThemeModeProvider>
      <DashboardLayoutContent>{children}</DashboardLayoutContent>
    </ThemeModeProvider>
  );
}

function DashboardLayoutContent({ children }) {
  const tokens = useTokens();
  const { collapsed, toggleCollapsed } = useToolBar();
  const location = useLocation();
  const theme = useTheme();
  // Below the md breakpoint the rail becomes an overlay drawer so the content
  // gets the full width.
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const section = location.pathname.split("/").filter(Boolean)[0];

  React.useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  const navigation = (
    <Box sx={{ paddingTop: "20px" }}>
      <ToolBarNavigation
        collapsed={isNarrow ? false : collapsed}
        onToggleCollapse={isNarrow ? () => setDrawerOpen(false) : toggleCollapsed}
      />
    </Box>
  );

  // Shares the bell's cached query, so the unread count shows in the tab title.
  const { id: viewerId } = useUser();
  const { unreadCount } = useNotifications(viewerId);

  React.useEffect(() => {
    const page = PAGE_TITLES[section];
    const title = page ? `${page} | Sunsure Advance Insights` : "Sunsure Advance Insights";
    document.title = unreadCount > 0 ? `(${unreadCount}) ${title}` : title;
  }, [section, unreadCount]);

  return (
    <Box
      sx={{
        bgcolor: tokens.appBg,
        height: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <SessionExpiryWarning />
      <KeyboardShortcuts />
      <Grid
        container
        direction="row"
        justifyContent="space-evenly"
        alignItems="stretch"
        wrap="nowrap"
        sx={{ height: "100%" }}
      >
        {isNarrow ? (
          <Drawer
            keepMounted
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { width: 272, bgcolor: tokens.sidebar } }}
          >
            {navigation}
          </Drawer>
        ) : (
          <Grid
            item
            sx={{
              height: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              flexShrink: 0,
              width: collapsed ? "80px" : "272px",
              transition: "width 0.2s ease-in-out",
              bgcolor: tokens.sidebar,
              borderRight: `1px solid ${tokens.border}`,
            }}
          >
            {navigation}
          </Grid>
        )}
        <Grid item sx={{ height: "100%", minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: isNarrow ? "100%" : collapsed ? "95vw" : "80vw",
              paddingX: isNarrow ? "12px" : 0,
              height: "100%",
              boxSizing: "border-box",
              margin: "0 auto",
              paddingTop: "12px",
              paddingBottom: "20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflow: "hidden",
              transition: "width 0.2s ease-in-out",
            }}
          >
            {isNarrow && (
              <Box sx={{ alignSelf: "flex-start", mb: 1 }}>
                <IconButton
                  aria-label="Open navigation"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ border: `1px solid ${tokens.border}`, bgcolor: tokens.card }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            )}
            <Breadcrumbs />
            <Box
              sx={{
                flex: "1 1 auto",
                minHeight: 0,
                width: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {children}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
